import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { motion, useInView } from 'framer-motion';
import TextReveal from '../components/TextReveal';
import { AnimatedDivider } from '../components/SVGAnimation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FadeInSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.23, 1, 0.32, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const About = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') console.error('Error fetching services:', error);
      }
    };
    fetchServices();
  }, []);

  const reasons = [
    {
      title: "Most creatives start with the brief. I start with the business problem.",
      description: "Before ideas, I look at the gap. Underutilised products. Market behaviour shifts. Is it penetration? Is it perception? Is it product understanding? Is it adoption? Clarity shapes creativity. And campaigns built that help move numbers, not just timelines."
    },
    {
      title: "Connected Thinking.",
      description: "Few campaigns fail because of a bad idea. They fail because the idea doesn't travel. I build work that travels, from film to feed, from outdoor to landing page, without losing its spine. The story stays consistent. The persuasion compounds. The response is measurable. Because attention is expensive. And every exposure should earn its keep."
    },
    {
      title: "No layered approvals. No inflated teams. No creative dilution.",
      description: "You work directly with the person shaping the idea, while I bring in the right specialists when scale demands it. That keeps the process honest, sharp, focussed. And the work accountable."
    }
  ];

  const awards = [
    { name: "Pitch BFSI Marketing Summit & Awards (2024)", wins: ["Most effective 360 degree marketing campaign", "Most effective use of Influencer Marketing", "Most effective use of TV", "Best Media Integration"] },
    { name: "Afaqs Marketing Excellence Awards 2024", wins: ["Best Online Integrated Campaign", "Best Campaign in BFSI"] },
    { name: "Indian Digital Marketing Awards (IDMA)", wins: ["Best Campaign - Short Ads", "Best Campaign - Direct Digital Response"] },
    { name: "ET Shark Awards", wins: ["Content Marketing", "Campaign Targeting Millennials"] },
    { name: "BW Merit Awards", wins: ["Best Use of Digital and Social Media"] },
    { name: "Elets India Brands Awards", wins: ["Best Use of Omni Channel Marketing BFSI", "Use of Integrated Marketing"] },
    { name: "Great Indian BFSI Awards", wins: ["Best Marketing Campaign of the Year", "Best Customer Education Initiative Campaign of the Year", "Best B2C Campaign of the Year", "Best BFSI Full Service Agency of the Year"] }
  ];

  return (
    <div className="min-h-screen pt-32 pb-0 px-6" data-testid="about-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-20">
          <motion.h1
            className="font-black"
            style={{ fontSize: 'clamp(4rem, 10vw, 12rem)', lineHeight: '0.9', color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
          >
            ABOUT
          </motion.h1>
        </div>

        {/* Bio */}
        <section className="mb-32" data-testid="bio-section">
          {[
            { type: 'p', text: "As a 90s kid, I grew up during a time when India and Indian advertising were changing rapidly." },
            { type: 'p', text: "The country was opening up. So were people\u2019s ambitions. And somewhere between cricket matches, cable television and winter afternoons at home, ads became a regular topic of conversation between my dad, my sister and me." },
            { type: 'p', text: "Long before I understood strategy or positioning, I was fascinated by why certain campaigns stayed with people, and why others disappeared within a week." },
            { type: 'group', lines: [
              "I didn\u2019t know then that I\u2019d eventually work in advertising.",
              "I just knew I couldn\u2019t stop writing."
            ]},
            { type: 'p', text: "Today, I work as a creative director and brand strategist, building campaigns designed to influence both perception and behaviour." },
            { type: 'group', lines: [
              "Most creatives begin with the brief.",
              "I prefer starting with the business problem."
            ]},
            { type: 'group', lines: [
              "An underutilised product.",
              "A shift in market behaviour.",
              "The gap between what a brand says and what people actually do."
            ]},
            { type: 'p', text: "That clarity shapes the work." },
            { type: 'p', text: "Over the last five years, I\u2019ve worked across BFSI, Commercial Real Estate, Automotive, FMCG, E-Commerce, Education, Mobile, and Beauty & Well-being, building integrated campaigns across film, digital, retail and brand experiences." },
            { type: 'p', text: "The work has gone on to receive 25+ industry recognitions, but I\u2019m still most interested in ideas that enter culture naturally. The kind people remember, repeat and return to." },
            { type: 'group', lines: [
              "Because good advertising gets noticed.",
              "The best kind becomes part of how people think."
            ]},
            { type: 'p', text: "Off-duty, I\u2019m usually hunting for old recipes, photographing everyday life, or reverse-engineering a film to understand why it stayed with people." }
          ].map((block, i) => (
            block.type === 'p' ? (
              <TextReveal
                key={i}
                as="p"
                className="text-lg leading-relaxed mt-8"
                style={{ color: 'var(--text-primary)' }}
                delay={Math.min(i, 5) * 0.05}
              >
                {block.text}
              </TextReveal>
            ) : (
              <div key={i} className="mt-8 space-y-1.5">
                {block.lines.map((ln, j) => (
                  <TextReveal
                    key={j}
                    as="p"
                    className="text-lg leading-relaxed"
                    style={{ color: 'var(--text-primary)' }}
                    delay={Math.min(i, 5) * 0.05 + j * 0.04}
                  >
                    {ln}
                  </TextReveal>
                ))}
              </div>
            )
          ))}

          {/* Stat strip */}
          <FadeInSection delay={0.2}>
            <div
              className="mt-20 pt-10 grid grid-cols-2 md:grid-cols-4 gap-8 border-t"
              style={{ borderColor: 'var(--border-color)' }}
              data-testid="bio-stats"
            >
              {[
                { value: "05", label: "Years" },
                { value: "50+", label: "Clients" },
                { value: "25+", label: "Awards" },
                { value: "08", label: "Industries" }
              ].map((stat, idx) => (
                <div key={idx}>
                  <div
                    className="font-bold leading-none mb-2"
                    style={{
                      fontSize: 'clamp(2.5rem, 4vw, 4rem)',
                      letterSpacing: '-0.03em',
                      color: 'var(--text-primary)',
                      fontFamily: 'Nohemi, sans-serif'
                    }}
                  >
                    {stat.value}
                  </div>
                  <div
                    className="text-xs uppercase tracking-[0.3em]"
                    style={{ color: '#C5A55A', fontFamily: 'Nohemi, sans-serif' }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </FadeInSection>
        </section>

        <AnimatedDivider className="mb-32" />

        {/* Services */}
        <section className="mb-32" data-testid="services-section">
          <FadeInSection>
            <h2
              className="font-black mb-16"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 5rem)', lineHeight: '1.0', fontWeight: '900', letterSpacing: '-0.03em', color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
            >
              <span className="accent-amethyst">What</span> I Do
            </h2>
          </FadeInSection>

          <div className="space-y-16">
            {services.map((service, index) => (
              <FadeInSection key={service.id} delay={index * 0.1}>
                <div
                  className="pl-8"
                  style={{ borderLeft: `3px solid ${index % 2 === 0 ? 'var(--accent-purple)' : 'var(--accent-gold)'}` }}
                >
                  <h3
                    className="text-lg font-bold mb-2"
                    style={{ color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
                  >
                    {service.title}
                  </h3>
                  <p className="text-base mb-4" style={{ color: 'var(--text-secondary)' }}>{service.description}</p>
                  <p className="text-base leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>{service.details}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </section>

        <AnimatedDivider className="mb-32" />

        {/* Reasons */}
        <section className="mb-32" data-testid="reasons-section">
          <FadeInSection>
            <h2
              className="font-black mb-16"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 5rem)', lineHeight: '1.0', fontWeight: '900', letterSpacing: '-0.03em', color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
            >
              <span className="accent-yellow">Reasons</span> to Work With Me
            </h2>
          </FadeInSection>

          <div className="space-y-16">
            {reasons.map((reason, index) => (
              <FadeInSection key={index} delay={index * 0.1}>
                <div
                  className="pl-8"
                  style={{ borderLeft: `3px solid ${index % 2 === 0 ? 'var(--accent-gold)' : 'var(--accent-purple)'}` }}
                >
                  <h3
                    className="text-lg font-bold mb-4"
                    style={{ color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
                  >
                    {reason.title}
                  </h3>
                  <p className="text-base leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>{reason.description}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </section>

        <AnimatedDivider className="mb-32" />

        {/* Awards */}
        <section className="mb-32" data-testid="awards-section">
          <FadeInSection>
            <h2
              className="font-black mb-16"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 5rem)', lineHeight: '1.0', fontWeight: '900', letterSpacing: '-0.03em', color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
            >
              Awards & <span className="accent-amethyst">Recognition</span>
            </h2>
          </FadeInSection>

          <div className="space-y-12">
            {awards.map((award, index) => (
              <FadeInSection key={index} delay={index * 0.08}>
                <div>
                  <h3
                    className="text-lg font-bold mb-4"
                    style={{ color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
                  >
                    {award.name}
                  </h3>
                  <ul className="space-y-2 pl-6">
                    {award.wins.map((win, idx) => (
                      <li key={idx} className="text-base list-disc" style={{ color: 'var(--text-tertiary)' }}>
                        {win}
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeInSection>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;
